import { createAppRequest, createParkRequest } from '@/services/request';
import type {
  EnterpriseBootstrapResponse,
  EnterpriseProfile,
  EnterpriseProfileSummary,
  SaveEnterpriseProfileParams,
} from './types/onboarding';
import type {
  EnterpriseContactInput,
  EnterpriseContact,
  EnterpriseContactPage,
  EnterpriseIntellectualPropertyInput,
  EnterpriseIntellectualProperty,
  EnterpriseIntellectualPropertyPage,
  EnterpriseMaterialHistory,
  EnterpriseMaterialRecord,
  EnterpriseMaterialSaveInput,
  EnterpriseProductInput,
  EnterpriseProduct,
  EnterpriseProductPage,
  EnterpriseProfilePageQuery,
  EnterpriseQualificationInput,
  EnterpriseQualification,
  EnterpriseQualificationPage,
} from './types/enterprise-profile';
import type { PageResult } from './types/common';
import type {
  ParkInvitationDetail,
  ParkInvitationDisplayStatus,
  ParkInvitationListResult,
} from './types/park-invitation';

export type ParkFileUpload = {
  uuid: string;
  fileName: string;
  fileSize: number;
  mimeType?: string;
};
export type AnnualIndicator = { uuid: string; name: string; code: string; dataType: 'text'|'number'|'date'|'boolean'; unit?: string; isRequired: boolean };
export type AnnualSubmission = { uuid: string; status: 'draft'|'submitted'|'returned'|'confirmed'; returnedReason?: string; values?: Array<{indicatorUuid:string;valueText?:string}>; evidences?: Array<{fileUuid:string;indicatorUuid?:string}> };
export type AnnualTask = { uuid:string; year:number; title:string; description?:string; deadlineAt?:string; submission?:AnnualSubmission };
export type AnnualTaskDetail = { task: AnnualTask; indicators: AnnualIndicator[]; submission: AnnualSubmission };

const request = createAppRequest();
const parkRequest = createParkRequest();

export const getEnterpriseBootstrap = () =>
  request<EnterpriseBootstrapResponse>('/bff/enterprise/bootstrap', {
    method: 'GET',
  });

export const listParkInvitations = (params?: {
  displayStatus?: ParkInvitationDisplayStatus;
  pageNumber?: number;
  pageSize?: number;
}) =>
  request<ParkInvitationListResult>('/bff/enterprise/park-invitations', {
    method: 'GET',
    params,
  });

export const getParkInvitation = (token: string) =>
  request<ParkInvitationDetail>(
    `/bff/enterprise/park-invitations/${encodeURIComponent(token)}`,
    { method: 'GET' },
  );

export const acceptParkInvitation = (token: string) =>
  request<unknown>(
    `/bff/enterprise/park-invitations/${encodeURIComponent(token)}/accept`,
    { method: 'POST' },
  );

export const rejectParkInvitation = (token: string) =>
  request<unknown>(
    `/bff/enterprise/park-invitations/${encodeURIComponent(token)}/reject`,
    { method: 'POST' },
  );

export const listEnterpriseProfiles = (tenantUuids: string[]) => {
  const ids = tenantUuids.map((item) => item.trim()).filter(Boolean);
  return request<EnterpriseProfileSummary[]>('/bff/enterprise/profiles', {
    method: 'GET',
    params: {
      tenantUuids: ids.join(','),
    },
  });
};

export const createEnterpriseProfile = (data: SaveEnterpriseProfileParams) =>
  request<EnterpriseProfile>('/bff/enterprise/profile', {
    method: 'POST',
    data,
  });

export const patchEnterpriseProfile = (
  data: Partial<SaveEnterpriseProfileParams>,
) =>
  request<EnterpriseProfile>('/bff/enterprise/profile', {
    method: 'PATCH',
    data,
  });

export const uploadParkFile = (
  file: File,
  scene:
    | 'enterprise_admission'
    | 'enterprise_exit'
    | 'enterprise_profile'
    | 'service_file'
    | 'annual_data_evidence',
  parkUuid?: string,
) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('scene', scene);
  return request<ParkFileUpload>('/bff/files', {
    method: 'POST',
    data: formData,
    requestType: 'form',
    ...(parkUuid
      ? {
          headers: {
            'x-park-uuid': parkUuid,
          },
        }
      : {}),
  });
};

export const getParkFile = (fileUuid: string) =>
  request<ParkFileUpload & { url?: string }>(`/bff/files/${fileUuid}`, {
    method: 'GET',
  });

export const listCurrentAnnualTasks = (params?: { pageNumber?: number; pageSize?: number }) =>
  parkRequest<{items: AnnualTask[]; pageNumber:number; pageSize:number; total:number}>('/bff/enterprise/current/annual-collection-tasks', { method:'GET', params });
export const getCurrentAnnualTask = (uuid:string) => parkRequest<AnnualTaskDetail>(`/bff/enterprise/current/annual-collection-tasks/${uuid}`, {method:'GET'});
export const saveCurrentAnnualSubmission = (uuid:string,data:{values:Array<{indicatorUuid:string;valueText?:string}>;evidences?:Array<{fileUuid:string;indicatorUuid?:string}>}) => parkRequest<AnnualSubmission>(`/bff/enterprise/current/annual-collection-tasks/${uuid}/submission`,{method:'POST',data});
export const submitCurrentAnnualSubmission = (uuid:string) => parkRequest<AnnualSubmission>(`/bff/enterprise/current/annual-collection-tasks/${uuid}/submission/submit`,{method:'POST'});

const currentEnterprisePath = '/bff/enterprise/current';

export const listCurrentEnterpriseContacts = (
  params?: EnterpriseProfilePageQuery,
) =>
  parkRequest<EnterpriseContactPage>(`${currentEnterprisePath}/contacts`, {
    method: 'GET',
    params,
  });

export const getCurrentEnterpriseContact = (uuid: string) =>
  parkRequest<EnterpriseContact>(`${currentEnterprisePath}/contacts/${uuid}`, {
    method: 'GET',
  });

export const createCurrentEnterpriseContact = (data: EnterpriseContactInput) =>
  parkRequest(`${currentEnterprisePath}/contacts`, { method: 'POST', data });

export const updateCurrentEnterpriseContact = (
  uuid: string,
  data: Partial<EnterpriseContactInput>,
) =>
  parkRequest(`${currentEnterprisePath}/contacts/${uuid}`, {
    method: 'PATCH',
    data,
  });

export const deleteCurrentEnterpriseContact = (uuid: string) =>
  parkRequest(`${currentEnterprisePath}/contacts/${uuid}`, { method: 'DELETE' });

export const listCurrentEnterpriseProducts = (
  params?: EnterpriseProfilePageQuery,
) =>
  parkRequest<EnterpriseProductPage>(`${currentEnterprisePath}/products`, {
    method: 'GET',
    params,
  });

export const getCurrentEnterpriseProduct = (uuid: string) =>
  parkRequest<EnterpriseProduct>(`${currentEnterprisePath}/products/${uuid}`, {
    method: 'GET',
  });

export const createCurrentEnterpriseProduct = (data: EnterpriseProductInput) =>
  parkRequest(`${currentEnterprisePath}/products`, { method: 'POST', data });

export const updateCurrentEnterpriseProduct = (
  uuid: string,
  data: Partial<EnterpriseProductInput>,
) =>
  parkRequest(`${currentEnterprisePath}/products/${uuid}`, {
    method: 'PATCH',
    data,
  });

export const deleteCurrentEnterpriseProduct = (uuid: string) =>
  parkRequest(`${currentEnterprisePath}/products/${uuid}`, { method: 'DELETE' });

export const listCurrentEnterpriseQualifications = (
  params?: EnterpriseProfilePageQuery,
) =>
  parkRequest<EnterpriseQualificationPage>(`${currentEnterprisePath}/qualifications`, {
    method: 'GET',
    params,
  });

export const getCurrentEnterpriseQualification = (uuid: string) =>
  parkRequest<EnterpriseQualification>(
    `${currentEnterprisePath}/qualifications/${uuid}`,
    { method: 'GET' },
  );

export const createCurrentEnterpriseQualification = (
  data: EnterpriseQualificationInput,
) => parkRequest(`${currentEnterprisePath}/qualifications`, { method: 'POST', data });

export const updateCurrentEnterpriseQualification = (
  uuid: string,
  data: Partial<EnterpriseQualificationInput>,
) =>
  parkRequest(`${currentEnterprisePath}/qualifications/${uuid}`, {
    method: 'PATCH',
    data,
  });

export const deleteCurrentEnterpriseQualification = (uuid: string) =>
  parkRequest(`${currentEnterprisePath}/qualifications/${uuid}`, {
    method: 'DELETE',
  });

export const listCurrentEnterpriseIntellectualProperties = (
  params?: EnterpriseProfilePageQuery,
) =>
  parkRequest<EnterpriseIntellectualPropertyPage>(
    `${currentEnterprisePath}/intellectual-properties`,
    { method: 'GET', params },
  );

export const getCurrentEnterpriseIntellectualProperty = (uuid: string) =>
  parkRequest<EnterpriseIntellectualProperty>(
    `${currentEnterprisePath}/intellectual-properties/${uuid}`,
    { method: 'GET' },
  );

export const createCurrentEnterpriseIntellectualProperty = (
  data: EnterpriseIntellectualPropertyInput,
) =>
  parkRequest(`${currentEnterprisePath}/intellectual-properties`, {
    method: 'POST',
    data,
  });

export const updateCurrentEnterpriseIntellectualProperty = (
  uuid: string,
  data: Partial<EnterpriseIntellectualPropertyInput>,
) =>
  parkRequest(`${currentEnterprisePath}/intellectual-properties/${uuid}`, {
    method: 'PATCH',
    data,
  });

export const deleteCurrentEnterpriseIntellectualProperty = (uuid: string) =>
  parkRequest(`${currentEnterprisePath}/intellectual-properties/${uuid}`, {
    method: 'DELETE',
  });

export const listCurrentEnterpriseMaterials = () =>
  parkRequest<EnterpriseMaterialRecord[] | PageResult<EnterpriseMaterialRecord>>(
    `${currentEnterprisePath}/materials`,
    { method: 'GET' },
  ).then((data) => (Array.isArray(data) ? data : data.items));

export const getCurrentEnterpriseMaterial = (templateUuid: string) =>
  parkRequest<EnterpriseMaterialRecord>(
    `${currentEnterprisePath}/materials/${templateUuid}`,
    { method: 'GET' },
  );

export const saveCurrentEnterpriseMaterial = (
  templateUuid: string,
  data: EnterpriseMaterialSaveInput,
) =>
  parkRequest(`${currentEnterprisePath}/materials/${templateUuid}`, {
    method: 'POST',
    data,
  });

export const deleteCurrentEnterpriseMaterial = (templateUuid: string) =>
  parkRequest(`${currentEnterprisePath}/materials/${templateUuid}`, {
    method: 'DELETE',
  });

export const listCurrentEnterpriseMaterialHistory = (templateUuid: string) =>
  parkRequest<EnterpriseMaterialHistory[]>(
    `${currentEnterprisePath}/materials/${templateUuid}/history`,
    { method: 'GET' },
  );
